import { useState, useEffect } from 'react';
import { Check, ChevronDown, Plus, Search } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useMembers } from '../../hooks/useQueries';
import type { Member } from '../../services';
import { QuickAddMember } from './QuickAddMember';

interface MemberSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function MemberSelect({ 
  value, 
  onValueChange, 
  placeholder = "เลือกสมาชิก...", 
  disabled = false,
  className 
}: MemberSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);

  // Fetch members
  const { data: membersData, isLoading } = useMembers();

  // Filter members based on search query
  const filteredMembers = membersData?.items.filter(member =>
    member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.phoneNumber?.includes(searchQuery) ||
    member.email?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Find selected member
  const selectedMember = membersData?.items.find(member => member.memberId === value);

  // Reset search when closing
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  const handleSelectMember = (memberId: string) => {
    onValueChange(memberId);
    setOpen(false);
  };

  const handleAddMember = () => {
    setShowAddMember(true);
    setOpen(false);
  };

  const handleMemberAdded = (newMember: Member) => {
    onValueChange(newMember.memberId);
    setShowAddMember(false);
  };

  return (
    <>
      <div className={cn("relative", className)}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            !value && "text-muted-foreground"
          )}
          onClick={() => setOpen(!open)}
          disabled={disabled}
        >
          {selectedMember ? selectedMember.name : placeholder}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>

        {open && (
          <div className="absolute top-full z-50 w-full mt-1 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="ค้นหาสมาชิก..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-60 overflow-auto p-1">
              {isLoading ? (
                <div className="py-6 text-center text-sm">กำลังโหลด...</div>
              ) : filteredMembers.length === 0 ? (
                <div className="space-y-1">
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    ไม่พบสมาชิก
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={handleAddMember}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    เพิ่มสมาชิกใหม่
                  </Button>
                </div>
              ) : (
                <>
                  {filteredMembers.map((member) => (
                    <div
                      key={member.memberId}
                      className={cn(
                        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                        value === member.memberId && "bg-accent"
                      )}
                      onClick={() => handleSelectMember(member.memberId)}
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        {value === member.memberId && (
                          <Check className="h-4 w-4" />
                        )}
                      </span>
                      <div className="flex flex-col">
                        <span>{member.name}</span>
                        {member.phoneNumber && (
                          <span className="text-xs text-muted-foreground">
                            {member.phoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div className="border-t pt-1 mt-1">
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={handleAddMember}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      เพิ่มสมาชิกใหม่
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Click outside to close */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Quick Add Member Modal */}
      <QuickAddMember
        open={showAddMember}
        onOpenChange={setShowAddMember}
        onMemberAdded={handleMemberAdded}
      />
    </>
  );
}